import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCcw, AlertCircle } from '../../ui/icons';
import Button from '../../ui/Button';
import Colors from '../../../constants/Colors';
import { opsHealthApi, OpsTipoffTaskLogEntry, OpsGameDataLogEntry } from '../../../services/api';
import LiveTipoffMqtt from './LiveTipoffMqtt';
import LiveGameDataMqtt from './LiveGameDataMqtt';

type LogsTab = 'task' | 'game-data';
type Direction = 'home' | 'away';

const TAB_LABELS: Record<LogsTab, string> = {
  task: 'Task Logs (radio-tipoff)',
  'game-data': 'Game Data (mqtt)',
};

/** One side (home or away) of the radio-tipoff task logs: its own CloudWatch history,
 * "Load older logs", and live MQTT feed merged into one newest-first list. Home and away
 * each get a panel so both run side by side and never share/mix log state. */
function TipoffDirectionPanel({
  gameName,
  direction,
  refreshKey,
}: {
  gameName: string;
  direction: Direction;
  /** Bumped by the parent's Refresh button to re-fetch this panel's history. */
  refreshKey: number;
}) {
  const [logs, setLogs] = useState<OpsTipoffTaskLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Bumped on every fetch/load-more so a superseded response (StrictMode double mount, fast
  // double-click) doesn't overwrite a newer result.
  const requestId = useRef(0);

  const fetchLogs = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    const response = await opsHealthApi.getTipoffTaskLogs(gameName, direction);
    if (id !== requestId.current) return;
    if (response.success && response.data?.success) {
      setError(null);
      setLogs([...(response.data.logs || [])].sort((a, b) => b.timestamp - a.timestamp));
      setNextToken(response.data.next_token || null);
    } else {
      setError(response.error || response.data?.error || 'Failed to load task logs');
      setLogs([]);
      setNextToken(null);
    }
    setLoading(false);
  }, [gameName, direction]);

  const loadMore = useCallback(async () => {
    if (!nextToken) return;
    const id = ++requestId.current;
    setLoadingMore(true);
    const response = await opsHealthApi.getTipoffTaskLogs(gameName, direction, 200, nextToken);
    if (id !== requestId.current) return;
    if (response.success && response.data?.success) {
      const older = response.data.logs || [];
      setLogs((prev) => {
        const seen = new Set(prev.map((p) => `${p.timestamp}|${p.message}`));
        const merged = [...prev, ...older.filter((l) => !seen.has(`${l.timestamp}|${l.message}`))];
        return merged.sort((a, b) => b.timestamp - a.timestamp);
      });
      setNextToken(response.data.next_token || null);
    } else {
      setError(response.error || response.data?.error || 'Failed to load older task logs');
    }
    setLoadingMore(false);
  }, [gameName, direction, nextToken]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshKey]);

  // Live MQTT lines land on top of the historical ones, newest first.
  const handleLiveMessage = useCallback((entry: OpsTipoffTaskLogEntry) => {
    setLogs((prev) => {
      if (prev.some((p) => p.timestamp === entry.timestamp && p.message === entry.message)) return prev;
      // Capped generously so live lines arriving after "Load older logs" don't truncate it.
      return [entry, ...prev].sort((a, b) => b.timestamp - a.timestamp).slice(0, 3000);
    });
  }, []);

  return (
    <div
      style={{
        minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px',
        border: '1px solid rgba(var(--color-border-rgb), 0.14)', borderRadius: '10px', background: '#FFFFFF', padding: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <span
          style={{
            fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            padding: '3px 10px', borderRadius: '999px',
            color: direction === 'home' ? Colors.primary : Colors.secondary,
            background: direction === 'home' ? 'rgba(var(--color-primary-rgb), 0.1)' : 'rgba(var(--color-secondary-rgb), 0.12)',
          }}
        >
          {direction === 'home' ? 'Home' : 'Away'}
        </span>
        <span style={{ fontSize: '11.5px', color: Colors.textLight }}>
          {loading ? 'Loading…' : `${logs.length} line${logs.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <LiveTipoffMqtt gameName={gameName} direction={direction} onMessage={handleLiveMessage} />

      <div style={{ height: '360px', overflow: 'auto', border: '1px solid rgba(var(--color-border-rgb), 0.12)', borderRadius: '8px', background: '#FFFFFF' }}>
        {error && (
          <div className="alert alert-warning" style={{ margin: '12px' }}>
            <AlertCircle size={18} />
            <div>{error}</div>
          </div>
        )}
        {!error && logs.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '24px', color: Colors.textLight, fontSize: '13px' }}>No log events.</div>
        )}
        {logs.length > 0 && (
          <div style={{ padding: '4px 0' }}>
            {logs.map((log, i) => (
              <div
                key={`${log.timestamp}-${i}`}
                style={{
                  display: 'flex', gap: '10px', padding: '4px 10px', fontFamily: "'SF Mono', monospace", fontSize: '12px',
                  borderBottom: i < logs.length - 1 ? '1px solid rgba(var(--color-border-rgb), 0.08)' : 'none',
                }}
              >
                <span style={{ color: Colors.textLight, flex: 'none', whiteSpace: 'nowrap' }}>{log.timestamp_iso}</span>
                <span style={{ color: Colors.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.message}</span>
              </div>
            ))}
            {nextToken && (
              <div style={{ textAlign: 'center', padding: '10px 12px' }}>
                <Button variant="outline" size="small" onClick={loadMore} loading={loadingMore}>
                  Load older logs
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface GameLogsAccordionProps {
  gameName: string;
  gameType: string;
}

/** Inline expandable logs section for a game card - tabs are fetched once on mount, newest
 * first. The Task Logs tab also carries the live radio-tipoff MQTT feed, above the historical
 * CloudWatch log lines for that same task. */
export default function GameLogsAccordion({ gameName, gameType }: GameLogsAccordionProps) {
  const [tab, setTab] = useState<LogsTab>('task');
  // Bumped by Refresh on the Task Logs tab; each direction panel re-fetches on change.
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);

  const [gameDataLogs, setGameDataLogs] = useState<OpsGameDataLogEntry[]>([]);
  const [gameDataError, setGameDataError] = useState<string | null>(null);
  const [gameDataLoading, setGameDataLoading] = useState(false);
  const [gameDataNextToken, setGameDataNextToken] = useState<string | null>(null);
  const [loadingMoreGameData, setLoadingMoreGameData] = useState(false);

  // Bumped on every fetch/load-more call so a response from a superseded request (e.g. React
  // StrictMode's dev-only double-invoked mount effect, or a fast double-click) can recognize
  // it's stale and skip applying its result over a newer one already in state.
  const gameDataRequestId = useRef(0);

  const fetchGameDataLogs = useCallback(async () => {
    const requestId = ++gameDataRequestId.current;
    setGameDataLoading(true);
    const response = await opsHealthApi.getGameDataLogs(gameName, gameType);
    if (requestId !== gameDataRequestId.current) return; // superseded by a newer fetch/load-more
    if (response.success && response.data?.success) {
      setGameDataError(null);
      setGameDataLogs([...(response.data.logs || [])].sort((a, b) => b.source_utc.localeCompare(a.source_utc)));
      setGameDataNextToken(response.data.next_token || null);
    } else {
      setGameDataError(response.error || response.data?.error || 'Failed to load game-data logs');
      setGameDataLogs([]);
      setGameDataNextToken(null);
    }
    setGameDataLoading(false);
  }, [gameName, gameType]);

  const loadMoreGameDataLogs = useCallback(async () => {
    if (!gameDataNextToken) return;
    const requestId = ++gameDataRequestId.current;
    setLoadingMoreGameData(true);
    const response = await opsHealthApi.getGameDataLogs(gameName, gameType, 200, gameDataNextToken);
    if (requestId !== gameDataRequestId.current) return;
    if (response.success && response.data?.success) {
      const older = response.data.logs || [];
      setGameDataLogs((prev) => {
        const seen = new Set(prev.map((p) => `${p.source_utc}|${p.home_score}|${p.guest_score}`));
        const merged = [...prev, ...older.filter((l) => !seen.has(`${l.source_utc}|${l.home_score}|${l.guest_score}`))];
        return merged.sort((a, b) => b.source_utc.localeCompare(a.source_utc));
      });
      setGameDataNextToken(response.data.next_token || null);
    } else {
      setGameDataError(response.error || response.data?.error || 'Failed to load older game-data records');
    }
    setLoadingMoreGameData(false);
  }, [gameName, gameType, gameDataNextToken]);

  useEffect(() => {
    fetchGameDataLogs();
    // Only when the accordion mounts for a (possibly new) game - Refresh re-fetches on demand.
    // Task logs are fetched by each TipoffDirectionPanel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameName, gameType]);

  // Same idea for the game-data (score/clock) feed: live records land on top of the initial
  // DynamoDB-fetched history, in the same list.
  const handleLiveGameData = useCallback((entry: OpsGameDataLogEntry) => {
    setGameDataLogs((prev) => {
      if (prev.some((p) => p.source_utc === entry.source_utc && p.home_score === entry.home_score && p.guest_score === entry.guest_score)) {
        return prev;
      }
      return [entry, ...prev].sort((a, b) => b.source_utc.localeCompare(a.source_utc)).slice(0, 3000);
    });
  }, []);

  const loading = tab === 'task' ? false : gameDataLoading;
  const refresh = () => (tab === 'task' ? setTaskRefreshKey((k) => k + 1) : fetchGameDataLogs());

  return (
    <div
      style={{
        border: '1px solid rgba(var(--color-border-rgb), 0.16)', borderRadius: '10px', background: Colors.background,
        padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '3px', background: '#FFFFFF', borderRadius: '999px', padding: '3px', flexWrap: 'wrap' }}>
          {(['task', 'game-data'] as LogsTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                height: '28px', padding: '0 12px', borderRadius: '999px', border: 0, cursor: 'pointer',
                fontSize: '11.5px', fontWeight: 700,
                background: tab === t ? Colors.background : 'transparent',
                color: tab === t ? Colors.text : Colors.textLight,
              }}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
        <Button variant="outline" size="small" onClick={refresh} loading={loading}>
          <RefreshCcw size={13} />
          Refresh
        </Button>
      </div>

      {/* Home and away radio-tipoff logs side by side (stacked on narrow screens). Kept
          mounted across tab switches so active MQTT connections survive - only hidden. */}
      <div className="tipoff-log-panels" style={{ display: tab === 'task' ? undefined : 'none' }}>
        <TipoffDirectionPanel gameName={gameName} direction="home" refreshKey={taskRefreshKey} />
        <TipoffDirectionPanel gameName={gameName} direction="away" refreshKey={taskRefreshKey} />
      </div>
      <div style={{ display: tab === 'game-data' ? 'block' : 'none' }}>
        <LiveGameDataMqtt gameName={gameName} onMessage={handleLiveGameData} />
      </div>

      <div style={{ display: tab === 'game-data' ? 'block' : 'none', maxHeight: '320px', overflow: 'auto', border: '1px solid rgba(var(--color-border-rgb), 0.12)', borderRadius: '8px', background: '#FFFFFF' }}>
        {tab === 'game-data' && (
          <>
            {gameDataError && (
              <div className="alert alert-warning" style={{ margin: '12px' }}>
                <AlertCircle size={18} />
                <div>{gameDataError}</div>
              </div>
            )}
            {!gameDataError && gameDataLogs.length === 0 && !gameDataLoading && (
              <div style={{ textAlign: 'center', padding: '24px', color: Colors.textLight, fontSize: '13px' }}>No records.</div>
            )}
            {gameDataLogs.length > 0 && (
              <table className="table" style={{ marginBottom: 0, tableLayout: 'fixed', minWidth: '640px' }}>
                <colgroup>
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '22%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', top: 0, background: '#FFFFFF', zIndex: 1 }}>Time (UTC)</th>
                    <th style={{ position: 'sticky', top: 0, background: '#FFFFFF', zIndex: 1 }}>Score</th>
                    <th style={{ position: 'sticky', top: 0, background: '#FFFFFF', zIndex: 1 }}>Period</th>
                    <th style={{ position: 'sticky', top: 0, background: '#FFFFFF', zIndex: 1 }}>Clock</th>
                    <th style={{ position: 'sticky', top: 0, background: '#FFFFFF', zIndex: 1 }}>Play Clock</th>
                    <th style={{ position: 'sticky', top: 0, background: '#FFFFFF', zIndex: 1 }}>Venue</th>
                  </tr>
                </thead>
                <tbody>
                  {gameDataLogs.map((log, i) => (
                    <tr key={`${log.source_utc}-${i}`}>
                      <td style={{ fontFamily: "'SF Mono', monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={log.source_utc}>{log.source_utc || '—'}</td>
                      <td style={{ fontFamily: "'SF Mono', monospace", whiteSpace: 'nowrap' }}>{log.home_score} – {log.guest_score}</td>
                      <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.period_text || log.period || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.clock || '—'}{log.clock_status ? ` (${log.clock_status})` : ''}</td>
                      <td style={{ fontFamily: "'SF Mono', monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.play_clock || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={log.venue_name}>{log.venue_name || '—'}</td>
                    </tr>
                  ))}
                  {gameDataNextToken && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '10px 12px' }}>
                        <Button variant="outline" size="small" onClick={loadMoreGameDataLogs} loading={loadingMoreGameData}>
                          Load older records
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
